// src/components/DetailsArticuloModal.jsx
import React, { useState, useEffect, useMemo } from "react";
import Modal from "react-modal";
import { pastelColors, tagTextColors } from "../hooks/useAddProductConfig";

const customStyles = {
  content: {
    position: "absolute",
    top: "50%",
    left: "52%",
    transform: "translate(-50%, -50%)",
    width: "90vw",
    height: "90vh",
    border: "none",
    borderRadius: 8,
    padding: 0,
    background: "#FFF",
    overflow: "hidden",
    fontFamily: "Inter, sans-serif",
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex: 2000,
  },
};

export default function DetailsArticuloModal({
  isOpen,
  onClose,
  product,
  gallery = [],
  related = [],
}) {
  const [activeTab, setActiveTab] = useState("description");
  const [images, setImages] = useState(gallery);
  const variantes = useMemo(
    () => product?.variantes || {},
    [product.variantes]
  );
  const [selectedVariants, setSelectedVariants] = useState({});

  useEffect(() => {
    setImages(gallery);
  }, [gallery]);

  useEffect(() => {
    const initial = {};
    Object.entries(variantes).forEach(([tipo, opciones]) => {
      initial[tipo] = opciones[0] || "";
    });
    setSelectedVariants(initial);
  }, [variantes]);

  const handleVariantSelect = (tipo, opcion) => {
    setSelectedVariants((prev) => ({
      ...prev,
      [tipo]: opcion,
    }));
  };

  const handleShare = () => {
    const shareData = {
      title: product.nombre,
      text: `Mira este producto: ${product.nombre}`,
      url: window.location.href,
    };

    if (navigator.share) {
      navigator
        .share(shareData)
        .catch((err) => console.error("Error compartiendo:", err));
    } else {
      navigator.clipboard
        .writeText(shareData.url)
        .then(() => alert("Enlace copiado al portapapeles"))
        .catch(() => alert("No se pudo copiar el enlace"));
    }
  };

  if (!isOpen || !product) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      ariaHideApp={false}
      overlayElement={(props, contentElement) => (
        <div {...props} style={{ ...props.style, overflow: "visible" }}>
          {/* Botón cerrar */}
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: 50,
              right: 50,
              background: "#EAEDF0",
              color: "#44566C",
              border: "none",
              borderRadius: "50%",
              width: 32,
              height: 32,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              lineHeight: 0,
              fontSize: 16,
              boxShadow: "0 1px 3px rgba(0,0,0,.12)",
              zIndex: 1,
            }}
            aria-label="Cerrar modal"
          >
            <i className="bi bi-x-lg" />
          </button>
          {contentElement}
        </div>
      )}
    >
      <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
        {/* Columna izquierda: galería */}
        <div
          style={{
            flex: "0 0 45%",
            boxSizing: "border-box",
            padding: "60px 32px 32px 60px",
            borderRight: "none",
            display: "flex",
            flexDirection: "column",
            overflow: "visible",
          }}
        >
          {/* Imagen principal */}
          <div
            style={{
              position: "relative",
              paddingTop: "75%",
              background: "#F3F4F6",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            <img
              src={images[0]}
              alt={product.nombre}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: 8,
              }}
            />
          </div>

          {/* Miniaturas */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 24,
              padding: "16px 0",
              background: "#FFF",
            }}
          >
            <button
              onClick={() =>
                setImages((imgs) => {
                  const copy = [...imgs];
                  copy.unshift(copy.pop());
                  return copy;
                })
              }
              style={{
                background: "none",
                border: "none",
                fontSize: 28,
                cursor: "pointer",
              }}
            >
              <i className="bi bi-chevron-left"></i>
            </button>

            {images.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`${product.nombre} miniatura ${i + 1}`}
                style={{
                  width: 64,
                  height: 64,
                  objectFit: "cover",
                  borderRadius: 4,
                  cursor: "pointer",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                }}
                onClick={() => {
                  setImages((imgs) => {
                    const copy = [...imgs];
                    copy.unshift(copy.splice(i, 1)[0]);
                    return copy;
                  });
                }}
              />
            ))}

            <button
              onClick={() =>
                setImages((imgs) => {
                  const copy = [...imgs];
                  copy.push(copy.shift());
                  return copy;
                })
              }
              style={{
                background: "none",
                border: "none",
                fontSize: 28,
                cursor: "pointer",
              }}
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>

        {/* Columna derecha: info + scroll */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            padding: 0,
          }}
        >
          {/* Header fijo (SIN borderBottom) */}
          <div
            style={{
              margin: 0,
              padding: "50px 42px 8px 42px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              background: "#FFF",
              boxSizing: "border-box",
            }}
          >
            <div>
              <h2 style={{ margin: 0 }}>{product.nombre}</h2>
              <p style={{ margin: "12px 0 0", color: "#6B7280" }}>
                Modelo: {product.modelo || "–"}
              </p>
            </div>
            <button
              onClick={handleShare}
              style={{
                background: "#0081FF",
                color: "#FFF",
                border: "none",
                borderRadius: 4,
                padding: "8px 16px",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <i
                className="bi bi-share"
                style={{ marginRight: 8, fontSize: 16 }}
              />
              Compartir
            </button>
          </div>

          {/* Cuerpo scrollable */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "8px 42px 32px 42px",
              background: "#FFF",
            }}
          >
            {/* === LÍNEA ALINEADA CON EL GUTTER (reemplaza el antiguo borderBottom del header) === */}
            <div
              style={{
                width: "100%",
                height: 0,
                borderBottom: "2px solid #E6EDFF",
                margin: "0 0 16px 0",
              }}
            />

            {/* Precio y badges */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 26,
                marginBottom: 24,
              }}
            >
              <span style={{ fontSize: 32, fontWeight: 700, color: "#0081FF" }}>
                {"$" +
                  (product.costo || 0).toLocaleString("es-MX", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                MXN
              </span>
            </div>

            {/* Línea divisora tras el precio */}
            <div
              style={{
                width: "100%",
                borderBottom: "2px solid #E6EDFF",
                margin: "16px 0",
              }}
            />

            {/* Variantes dinámicas */}
            {Object.entries(variantes).map(([tipo, opciones]) => (
              <div
                key={tipo}
                style={{
                  margin: "24px 0 32px",
                  paddingBottom: 36,
                  borderBottom: "2px solid #E6EDFF",
                }}
              >
                <p
                  style={{
                    margin: "0 0 8px",
                    fontWeight: 600,
                    color: "#9CA3AF",
                  }}
                >
                  {tipo}
                </p>
                <div style={{ display: "flex", gap: 16 }}>
                  {opciones.map((opcion) => {
                    const isSelected = selectedVariants[tipo] === opcion;
                    return (
                      <label
                        key={opcion}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          cursor: "pointer",
                          background: isSelected ? "#EDF0F8" : "#F3F3F3",
                          padding: "8px 16px",
                          borderRadius: 4,
                        }}
                      >
                        <input
                          type="radio"
                          name={tipo}
                          value={opcion}
                          checked={isSelected}
                          onChange={() => handleVariantSelect(tipo, opcion)}
                          style={{ accentColor: "#0081FF" }}
                        />
                        <span
                          style={{
                            marginLeft: 4,
                            color: isSelected ? "#3A4980" : "#374151",
                          }}
                        >
                          {opcion}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Stock */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: 16,
                border: "2px solid #E6EDFF",
                borderRadius: 8,
              }}
            >
              <span style={{ fontSize: 22 }}>📦</span>
              <div>
                <p style={{ margin: 0, fontWeight: 600 }}>Almacén</p>
                <span style={{ fontSize: 12, color: "#6B7280" }}>
                  {product.existencias ?? 0} piezas en existencia
                </span>
              </div>
            </div>

            {/* Línea divisora azul sobre pestañas */}
            <div
              style={{
                width: "100%",
                borderBottom: "2px solid #E6EDFF",
                margin: "16px 0",
                paddingBottom: 0,
              }}
            />

            {/* Pestañas */}
            <div>
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                {["description", "specs", "notes"].map((tab) => {
                  const isActive = activeTab === tab;
                  const label =
                    tab === "description"
                      ? "Descripción"
                      : tab === "specs"
                      ? "Especificaciones"
                      : "Notas";
                  return (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      style={{
                        padding: "8px 16px",
                        background: isActive ? "#0081FF" : "#FFF",
                        color: isActive ? "#FFF" : "#333",
                        border: isActive ? "none" : "1px solid #E5E7EB",
                        borderRadius: 4,
                        cursor: "pointer",
                        fontWeight: isActive ? 600 : 400,
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              {/* Contenido según pestaña */}
              <div style={{ color: "#333", lineHeight: 1.6 }}>
                {activeTab === "description" && (
                  <div
                    style={{
                      textAlign: "justify",
                      lineHeight: 1.4,
                      margin: 0,
                      padding: 0,
                    }}
                    dangerouslySetInnerHTML={{ __html: product.descripcion }}
                  />
                )}
                {activeTab === "specs" && (
                  <div
                    style={{
                      textAlign: "justify",
                      lineHeight: 1.4,
                      margin: 0,
                      padding: 0,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: product.especificaciones,
                    }}
                  />
                )}
                {activeTab === "notes" && (
                  <div
                    style={{
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.6,
                      margin: 0,
                    }}
                    dangerouslySetInnerHTML={{ __html: product.notas }}
                  />
                )}
              </div>
            </div>

            {/* Etiquetas */}
            {Array.isArray(product.etiquetas) &&
              product.etiquetas.length > 0 && (
                <div style={{ marginTop: 32, marginBottom: 32 }}>
                  <h4 style={{ marginBottom: 12 }}>Etiquetas</h4>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {product.etiquetas.map((tag, i) => (
                      <span
                        key={`${tag}-${i}`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          height: 28,
                          padding: "0 10px",
                          borderRadius: 4,
                          fontSize: 12,
                          fontWeight: 700,
                          backgroundColor:
                            pastelColors[i % pastelColors.length],
                          color: tagTextColors[i % tagTextColors.length],
                          boxShadow: "0 2px 4px rgba(0,0,0,.04)",
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

            {/* Productos Relacionados */}
            {related.length > 0 && (
              <div style={{ marginBottom: 32 }}>
                <h4 style={{ marginBottom: 16 }}>Productos Relacionados</h4>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))",
                    gap: 16,
                  }}
                >
                  {related.map((rp) => (
                    <div
                      key={rp.id}
                      style={{
                        border: "1px solid #E5E7EB",
                        borderRadius: 8,
                        overflow: "hidden",
                        cursor: "pointer",
                        background: "#FFF",
                      }}
                    >
                      <img
                        src={
                          rp.imagen ||
                          (Array.isArray(rp.imagenes)
                            ? rp.imagenes[0]
                            : null) ||
                          "/img/no-data.svg"
                        }
                        alt={rp.nombre}
                        style={{
                          width: "100%",
                          height: 120,
                          objectFit: "cover",
                        }}
                      />
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: 12,
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                            margin: 0,
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#868686",
                            lineHeight: "1.2em",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {rp.nombre}
                        </span>
                        <span
                          style={{
                            margin: 0,
                            marginLeft: 12,
                            fontSize: "14px",
                            color: "#3C3C3C",
                            whiteSpace: "nowrap",
                            fontWeight: "bold",
                          }}
                        >
                          $
                          {Number(rp.costo || 0).toLocaleString("es-MX", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
